import IndividualContactMainData from "./IndividualContactMainData";
import IndividualCommunication from "./IndividualCommunication";
import {CreateIndividualContactApiDto} from "../../../api/model/rest";
import JsonConverter from "../../../api/helper/JsonConverter";
import {Option} from "ts-option";
import DateUtils from "../../../utils/DateUtils";
import moment = require("moment");

/**
 * Individual Contact data.
 */
export default class IndividualContactData {

	/**
	 * String using to search a contact.
	 */
	public readonly searchString: string;

	/**
	 * @param {IndividualContactMainData} mainData main data of the contact.
	 * @param {IndividualCommunication} communication communication channels.
	 * @param {string} primarySiteId id of a primary site.
	 * @param {Option<boolean>} communicationConsent user marketing comms consent.
	 * @param {Option<boolean>} servicePlanRemindersConsent user service reminders consent.
	 * @param {moment.Moment} creationDateTime timestamp of contact creation.
	 * @param {string} brandId
	 * @param {Option<string>} partnerBrandId
	 * @param {boolean} archived
	 */
	constructor(public readonly mainData: IndividualContactMainData,
		public readonly communication: IndividualCommunication,
		public readonly primarySiteId: Option<string>,
		public readonly communicationConsent: Option<boolean>,
		public readonly servicePlanRemindersConsent: Option<boolean>,
		public readonly creationDateTime: moment.Moment,
		public readonly brandId: string,
		public readonly partnerBrandId: Option<string>,
		public readonly archived: boolean) {

		this.searchString = `${mainData.searchString} ${communication.searchString}`.toLowerCase()
	}

	toJson(): CreateIndividualContactApiDto {
		const json: CreateIndividualContactApiDto = {
			primarySiteId: this.primarySiteId.orNull,
			emails: this.communication.emails.getOrElse([]).map(e => e.value),
			title: this.mainData.title.orNull,
			firstName: this.mainData.firstName,
			lastName: this.mainData.lastName,
			phoneNumbers: this.communication.phoneNumbers.getOrElse([]).map(pn => pn.toJson()),
			dateOfBirth: this.mainData.dateOfBirth.map(m => m.format(DateUtils.ISO_DATE_FORMAT)).orNull,
			brandId: this.brandId,
			partnerBrandId: this.partnerBrandId.orNull
		};
		return JsonConverter.removeEmptyFields(json)
	}

	/**
	 * Returns contact display name.
	 * @param {boolean} withStatus add archived description if true
	 * @returns {string}
	 */
	displayName(withStatus?: boolean): string {
		const title = this.mainData.title.map((title) => `${title} `).getOrElse("");
		const displayName = `${title}${this.mainData.firstName} ${this.mainData.lastName}`;
		return `${withStatus && this.archived ? `${displayName} (archived)` : displayName}`;
	}

	/**
	 * Get full details of the contact.
	 * @return {string}
	 */
	fullDetails(): string {
		const phoneNumbers = this.communication.phoneNumbers.getOrElse([]).map(n => n.nationalPhoneNumber);
		const emails = this.communication.emails.getOrElse([]).map(e => e.value);
		const phoneNumbersStr = phoneNumbers.length ? `${phoneNumbers.join("\n")}\n` : "";
		const emailsStr = emails.length ? `${emails.join("\n")}\n` : "";
		return `${this.displayName()}${phoneNumbers.length || emails.length ? "\n" : ""}${phoneNumbersStr}${emailsStr}`;
	}

	copy({
		mainData = this.mainData,
		communication = this.communication,
		primarySiteId = this.primarySiteId,
		communicationConsent = this.communicationConsent,
		servicePlanRemindersConsent = this.servicePlanRemindersConsent,
		creationDateTime = this.creationDateTime,
		brandId = this.brandId,
		partnerBrandId = this.partnerBrandId,
		archived = this.archived
	}) {
		return new IndividualContactData(mainData, communication, primarySiteId, communicationConsent,
			servicePlanRemindersConsent, creationDateTime, brandId, partnerBrandId, archived)
	}
}
